/**
 * Choosing an audit's analyses — one table for New audit's step 3 and for
 * "Edit analyses" on an existing audit, so the two read and behave alike.
 *
 * Ready / Blocked comes from the backend: for New audit, against the data
 * scope being built; for an existing audit, against its frozen data.
 */

import { useEffect, useMemo, useState } from 'react';
import { useModule } from '../modules/useModule';
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import { Dialog, ErrorNotice, Note } from '../../shared/ui';
import { DataTable, useFullScreen, type Column } from '../shared/tables';
import { ErrorState } from '../shared/states';
import { AnalysisStatusTag } from '../analyses/AnalysisStatusTag';
import { dataTypeLabel } from '../analyses/analysisRows';
import {
	useAnalysisCatalogueQuery,
	useAnalysisOverviewQuery,
	useAuditAnalysisAvailabilityQuery,
} from '../../queries/analysis';
import { useUpdateAuditAnalysesMutation } from '../../queries/audits';
import { useToast } from '../../provider/toast';
import { count } from '../../utils/format';
import type {
	AnalysisAvailabilityResult,
	AnalysisDefinition,
} from '../../schemas';

const NONE: ReadonlySet<string> = new Set();

/* Secondary text in a cell: smaller and quieter than the value beside it. */
const NOTE_SX = { fontSize: 'var(--fs-small)', color: 'var(--ink-3)' } as const;

/**
 * The analyses table a person ticks. A blocked analysis cannot be chosen —
 * except, when editing, one the audit already has (`keep`), which may stay.
 * A search above it narrows the list; "choose every" then ticks only what the
 * search shows.
 */
export function AnalysisPicker({
	analyses,
	chosen,
	onChange,
	availability,
	availabilityLoading,
	loading,
	keep = NONE,
}: {
	analyses: AnalysisDefinition[];
	chosen: ReadonlySet<string>;
	onChange: (next: Set<string>) => void;
	availability: ReadonlyMap<string, AnalysisAvailabilityResult>;
	availabilityLoading: boolean;
	loading: boolean;
	keep?: ReadonlySet<string>;
}) {
	const [search, setSearch] = useState('');
	const { module } = useModule();
	const full = useFullScreen('Analyses');
	const needle = search.trim().toLowerCase();
	const shown = needle
		? analyses.filter((item) =>
				`${item.code} ${item.name} ${item.blurb}`
					.toLowerCase()
					.includes(needle),
			)
		: analyses;

	const locked = (slug: string) =>
		availability.get(slug)?.availability === 'BLOCKED' && !keep.has(slug);
	/* What "choose every" ticks: every ready analysis shown, plus any the audit
     keeps. Ticks outside the search are left as they are. */
	const selectable = shown
		.map((item) => item.slug)
		.filter(
			(slug) =>
				!locked(slug) &&
				(availability.get(slug)?.availability === 'READY' ||
					keep.has(slug)),
		);
	const allOn =
		selectable.length > 0 && selectable.every((slug) => chosen.has(slug));
	const someOn = selectable.some((slug) => chosen.has(slug));
	const toggle = (slug: string) => {
		const next = new Set(chosen);
		if (next.has(slug)) next.delete(slug);
		else next.add(slug);
		onChange(next);
	};

	const columns: Array<Column<AnalysisDefinition>> = [
		{
			key: 'pick',
			width: 48,
			label: (
				<Checkbox
					size="small"
					checked={allOn}
					indeterminate={someOn && !allOn}
					disabled={selectable.length === 0}
					inputProps={{ 'aria-label': 'Choose every ready analysis' }}
					onClick={(event) => event.stopPropagation()}
					onChange={(event) => {
						const next = new Set(chosen);
						for (const slug of selectable)
							if (event.target.checked) next.add(slug);
							else next.delete(slug);
						onChange(next);
					}}
				/>
			),
			render: (item) => {
				const blocked = locked(item.slug);
				return (
					<span
						title={
							blocked
								? 'Blocked analyses cannot be chosen.'
								: undefined
						}
					>
						<Checkbox
							size="small"
							checked={chosen.has(item.slug)}
							disabled={blocked}
							inputProps={{ 'aria-label': `Choose ${item.name}` }}
							onClick={(event) => event.stopPropagation()}
							onChange={() => toggle(item.slug)}
						/>
					</span>
				);
			},
		},
		{
			key: 'ref',
			label: 'Ref.',
			width: 104,
			sortValue: (item) => item.code,
			render: (item) => (
				<Box component="span" sx={{ whiteSpace: 'nowrap' }}>
					{item.code}
				</Box>
			),
		},
		{
			/* No fixed width: the description takes the room the others leave, so
         the table fits a dialog without scrolling sideways. */
			key: 'name',
			label: 'Analysis',
			sortValue: (item) => item.name,
			render: (item) => (
				<Typography
					component="span"
					sx={{ fontSize: 'inherit', lineHeight: 'var(--lh-snug)' }}
				>
					<Box
						component="span"
						sx={{ fontWeight: 600, color: 'var(--ink)' }}
					>
						{item.name}
						{item.blurb ? ':' : ''}
					</Box>{' '}
					<Box component="span" sx={NOTE_SX}>
						{item.blurb}
					</Box>
				</Typography>
			),
		},
		{
			key: 'data',
			label: 'Data',
			width: 120,
			sortValue: (item) =>
				item.dataType ? dataTypeLabel(item.dataType, module) : '',
			render: (item) =>
				item.dataType ? dataTypeLabel(item.dataType, module) : '—',
		},
		{
			key: 'availability',
			label: 'Availability',
			width: 170,
			render: (item) => (
				<AvailabilityCell
					loading={availabilityLoading}
					info={availability.get(item.slug)}
				/>
			),
		},
	];

	return (
		<Stack gap="var(--s2)">
			{/* Search on the left, full screen on the right: one row above the table. */}
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: 'var(--s2)',
					flexWrap: 'wrap',
				}}
			>
				<TextField
					type="search"
					size="small"
					placeholder="Search ref. or analysis"
					value={search}
					onChange={(event) => setSearch(event.target.value)}
					slotProps={{
						htmlInput: { 'aria-label': 'Search analyses' },
					}}
					sx={{ width: 280 }}
				/>
				{full.button}
			</Box>
			<DataTable
				rows={shown}
				columns={columns}
				rowKey={(item) => item.slug}
				caption="Analyses"
				fullScreen={full}
				loading={loading}
				selected={(item) => chosen.has(item.slug)}
				onRowClick={(item) => {
					if (!locked(item.slug)) toggle(item.slug);
				}}
				initialSort={{ key: 'ref', direction: 'asc' }}
				maxHeight="compact"
				empty="No analysis matches that search."
			/>
		</Stack>
	);
}

/**
 * "✓ Ready" or "⚠ Blocked" plus why — docs/analyses-reference.md spec §8/§9. The
 * backend decides Ready/Blocked; the word and tone come from the same
 * `analysisStatusPresentation` the Analyses list and Analysis page use.
 */
function AvailabilityCell({
	loading,
	info,
}: {
	loading: boolean;
	info: AnalysisAvailabilityResult | undefined;
}) {
	/* Null availability = not checked yet (no data scope): nothing to say. */
	if (!info || info.availability === null) {
		return loading ? (
			<CircularProgress size={12} aria-label="Loading" />
		) : (
			<Typography component="span" sx={NOTE_SX}>
				—
			</Typography>
		);
	}
	const tag = (
		<AnalysisStatusTag row={{ availability: info.availability }} glyph />
	);
	if (info.availability === 'READY') return tag;
	/* Why, in a few words: the missing fields, or the reason the backend gave. */
	const why =
		info.missingColumns.length > 0
			? `Needs: ${info.missingColumns.map((column) => column.label).join(', ')}`
			: info.blockReason;
	return (
		<Stack spacing={0.5} alignItems="flex-start">
			{tag}
			{why && (
				<Typography component="span" sx={NOTE_SX}>
					{why}
				</Typography>
			)}
		</Stack>
	);
}

/**
 * "Edit analyses" — change which analyses an existing audit runs. Starts from
 * the audit's own selection; saving drops a removed analysis together with its
 * results and exceptions (said before saving) and adds a new one as not run.
 * An analysis the audit has that is no longer offered stays as it is.
 */
export function EditAnalysesDialog({
	open,
	auditId,
	onClose,
}: {
	open: boolean;
	auditId: string;
	onClose: () => void;
}) {
	const toast = useToast();
	/* The audit page follows the audit's module, so the URL's is the audit's. */
	const { module } = useModule();
	const catalogue = useAnalysisCatalogueQuery(module);
	const overview = useAnalysisOverviewQuery(auditId);
	const availability = useAuditAnalysisAvailabilityQuery(auditId, open);
	const save = useUpdateAuditAnalysesMutation(auditId);

	const analyses = useMemo(() => catalogue.data ?? [], [catalogue.data]);
	const rows = useMemo(() => overview.data?.rows ?? [], [overview.data]);
	const current = useMemo(() => new Set(rows.map((row) => row.slug)), [rows]);
	const availabilityBySlug = useMemo(
		() =>
			new Map(
				availability.data?.analyses.map((item) => [item.slug, item]) ??
					[],
			),
		[availability.data],
	);

	/* Filled once per opening from the audit's own selection — not again on a
     poll, which would undo the person's ticks. */
	const [chosen, setChosen] = useState<Set<string>>(new Set());
	const [seeded, setSeeded] = useState(false);
	useEffect(() => {
		if (!open) setSeeded(false);
	}, [open]);
	useEffect(() => {
		if (!open || seeded || !overview.data) return;
		setChosen(new Set(overview.data.rows.map((row) => row.slug)));
		save.reset();
		setSeeded(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, seeded, overview.data]);

	const offered = new Set(analyses.map((item) => item.slug));
	/* Held back from the catalogue, so not in the table: kept as they are. */
	const kept = [...current].filter((slug) => !offered.has(slug));
	const removed = rows.filter(
		(row) => offered.has(row.slug) && !chosen.has(row.slug),
	);
	const added = [...chosen].filter((slug) => !current.has(slug));
	const changed = removed.length > 0 || added.length > 0;
	/* Removing these loses something: a result, and any exceptions it raised. */
	const losing = removed.filter((row) => row.hasResult);

	const submit = () =>
		save.mutate([...chosen, ...kept], {
			onSuccess: () => {
				toast('ok', 'Analyses updated.');
				onClose();
			},
		});

	return (
		<Dialog
			open={open}
			onClose={onClose}
			title="Edit analyses"
			subtitle="Choose which analyses this audit runs."
			width={1000}
			plainFooter
			footer={
				<Box
					sx={{
						display: 'flex',
						gap: 'var(--s2)',
						justifyContent: 'flex-end',
					}}
				>
					<Button
						variant="outlined"
						onClick={onClose}
						disabled={save.isPending}
					>
						Cancel
					</Button>
					<Button
						variant="contained"
						loading={save.isPending}
						disabled={
							!changed || chosen.size === 0 || save.isPending
						}
						title={
							chosen.size === 0
								? 'Choose at least one analysis.'
								: !changed
									? 'Nothing has changed yet.'
									: undefined
						}
						onClick={submit}
					>
						Save analyses
					</Button>
				</Box>
			}
		>
			<Stack gap="var(--s3)">
				<Typography
					sx={{ fontSize: 'var(--fs-small)', color: 'var(--ink-3)' }}
				>
					{count(chosen.size)} of {count(analyses.length)} chosen. An
					added analysis starts as not run — Run analyses runs it.
				</Typography>
				{losing.length > 0 && (
					<Note tone="warn" row title="These have results">
						Removing{' '}
						{losing
							.map((row) => `${row.ref} ${row.name}`)
							.join(', ')}{' '}
						also removes their results and any exceptions they
						raised.
					</Note>
				)}
				<ErrorState
					error={catalogue.error}
					onRetry={catalogue.refetch}
				/>
				<ErrorState
					error={availability.error}
					onRetry={availability.refetch}
				/>
				<ErrorNotice error={save.error} />
				{seeded ? (
					<AnalysisPicker
						analyses={analyses}
						chosen={chosen}
						onChange={setChosen}
						availability={availabilityBySlug}
						availabilityLoading={availability.isPending}
						loading={catalogue.isPending}
						keep={current}
					/>
				) : (
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'center',
							p: 'var(--s6)',
						}}
					>
						<CircularProgress size={24} aria-label="Loading" />
					</Box>
				)}
			</Stack>
		</Dialog>
	);
}
